// =======================================================
// MatrixRain — vertical falling glyph streams (Matrix code)
// =======================================================
//
// Fullscreen fragment shader. The screen is divided into a character grid;
// each column has its own falling "head" with a fading trail above it.
// Glyphs are procedural 5x7 bit patterns reseeded on a slow tick so they
// flicker like the film effect without needing a font atlas.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uResolution;
  uniform float uCols;
  uniform float uSpeed;
  uniform float uTrail;
  uniform float uDensity;
  uniform float uChangeRate;
  uniform float uGlow;
  uniform vec3  uColor;
  uniform vec3  uHeadColor;
  uniform vec3  uBg;

  ${GLSL_COMMON}

  void main() {
    float aspect = uResolution.x / uResolution.y;
    float cols = max(4.0, floor(uCols));
    float cellPx = uResolution.x / cols;
    float rows = max(4.0, floor(uResolution.y / cellPx));

    vec2 grid = vec2(vUv.x * cols, vUv.y * rows);
    vec2 cell = floor(grid);
    vec2 local = fract(grid);

    // 0 at top, rows-1 at bottom (vUv.y=1 is top in our plane)
    float rowFromTop = (rows - 1.0) - cell.y;

    // Per-column randomness
    float colSeed = hash11(cell.x * 1.371 + 0.13);
    float speed = mix(0.45, 1.6, colSeed) * uSpeed;
    float offset = hash11(cell.x * 7.77 + 3.1) * 1000.0;
    // Some columns are "dead" (no stream) controlled by density
    float alive = step(1.0 - uDensity, hash11(cell.x * 2.913 + 9.4));

    float cycle = rows + uTrail + 6.0;
    float headFromTop = mod(uTime * speed * 6.0 + offset, cycle) - 3.0;

    float trailDist = headFromTop - rowFromTop;
    float inTrail = step(0.0, trailDist) * step(trailDist, uTrail);
    float fade = 1.0 - clamp(trailDist / max(uTrail, 0.0001), 0.0, 1.0);
    fade = pow(fade, 1.6);

    // Head highlight (within ~1 cell of the head)
    float headMask = smoothstep(1.2, 0.0, trailDist);

    // Per-cell glyph: 5x7 bit pattern, reseeded on a slow tick
    vec2 pix = floor(local * vec2(5.0, 7.0));
    float tick = floor(uTime * uChangeRate + colSeed * 30.0 + rowFromTop * 0.41);
    float h = hash21(cell * vec2(1.31, 7.71) + pix * vec2(13.7, 5.3) + tick);
    float on = step(0.5, h);

    // small inner padding so glyphs don't fully tile
    float pad = step(0.05, local.x) * step(local.x, 0.95)
              * step(0.05, local.y) * step(local.y, 0.95);
    on *= pad;

    float lit = on * inTrail * alive;

    vec3 trailCol = mix(uColor * 0.15, uColor, fade);
    vec3 col = mix(trailCol, uHeadColor, headMask);
    vec3 outCol = uBg + col * lit;

    // soft column glow when head is near (cheap bloom hint)
    float glow = headMask * uGlow * alive * 0.25;
    outCol += uColor * glow * (1.0 - smoothstep(0.0, 0.5, abs(local.x - 0.5)));

    gl_FragColor = vec4(outCol, 1.0);
  }
`;

export class MatrixRain extends Generator {
  constructor() {
    super({
      name: 'Matrix Rain',
      category: 'intense',
      schema: {
        cols:       { default: 90,  min: 20,  max: 240, step: 1,    label: 'Columns' },
        speed:      { default: 0.6, min: 0.05, max: 3,  step: 0.01, label: 'Speed' },
        trail:      { default: 22,  min: 4,    max: 60, step: 1,    label: 'Trail length' },
        density:    { default: 0.85,min: 0.1,  max: 1,  step: 0.01, label: 'Density' },
        changeRate: { default: 6.0, min: 0,    max: 30, step: 0.1,  label: 'Glyph change rate' },
        glow:       { default: 1.0, min: 0,    max: 4,  step: 0.05, label: 'Head glow' },
        color:      { default: '#22ff66', type: 'color', label: 'Trail color' },
        headColor:  { default: '#eaffea', type: 'color', label: 'Head color' },
        bg:         { default: '#000000', type: 'color', label: 'Background' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime:       { value: 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uCols:       { value: this.v('cols') },
        uSpeed:      { value: this.v('speed') },
        uTrail:      { value: this.v('trail') },
        uDensity:    { value: this.v('density') },
        uChangeRate: { value: this.v('changeRate') },
        uGlow:       { value: this.v('glow') },
        uColor:      { value: new THREE.Color(this.v('color')) },
        uHeadColor:  { value: new THREE.Color(this.v('headColor')) },
        uBg:         { value: new THREE.Color(this.v('bg')) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    switch (key) {
      case 'cols':       u.uCols.value = val; break;
      case 'speed':      u.uSpeed.value = val; break;
      case 'trail':      u.uTrail.value = val; break;
      case 'density':    u.uDensity.value = val; break;
      case 'changeRate': u.uChangeRate.value = val; break;
      case 'glow':       u.uGlow.value = val; break;
      case 'color':      u.uColor.value.set(val); break;
      case 'headColor':  u.uHeadColor.value.set(val); break;
      case 'bg':         u.uBg.value.set(val); break;
    }
  }

  update(dt, time) {
    if (this.mesh) this.mesh.material.uniforms.uTime.value = time;
  }
}
